import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Lightbulb, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { suggestTemplate, type Category } from '../../../services/templateService';
import { useAuth } from '@/contexts/AuthContext';
import { useTenancy } from '@/contexts/TenancyContext';

interface TemplateSuggestionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: Category[];
}

export default function TemplateSuggestionDialog({ 
  open, 
  onOpenChange, 
  categories 
}: TemplateSuggestionDialogProps) {
  const { user } = useAuth();
  const { activeTenancy } = useTenancy();
  
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Template name must be at least 3 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    // Category validation removed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!activeTenancy?.id) {
      toast.error('No organization selected');
      return;
    }

    setIsSubmitting(true);

    try {
      await suggestTemplate({
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: '', // Sending empty string for category
        organizationId: activeTenancy.id,
        userEmail: user?.email,
        userName: user?.displayName || user?.email
      });

      toast.success('Template request submitted successfully!', {
        description: 'Thank you for your request. Our team will review it shortly.'
      });

      // Reset form and close dialog
      setFormData({ name: '', description: '' });
      setErrors({});
      onOpenChange(false);

    } catch (error) {
      console.error('Error submitting template request:', error);
      toast.error('Failed to submit request', {
        description: error instanceof Error ? error.message : 'Please try again later'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({ name: '', description: '' });
      setErrors({});
      onOpenChange(false);
    }
  };

  // Flatten categories for the select dropdown
  const flatCategories = categories.reduce<Array<{ id: string; name: string; groupName: string }>>((acc, category) => {
    // Add the main category
    acc.push({
      id: category.id,
      name: category.name,
      groupName: category.groupName
    });
    
    // Add subcategories if they exist
    if (category.items) {
      category.items.forEach(item => {
        acc.push({
          id: item.id,
          name: `${category.name} > ${item.name}`,
          groupName: category.groupName
        });
      });
    }
    
    return acc;
  }, []);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-yellow-500" />
            Request a Template
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            {/* Template Name */}
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                placeholder="e.g., Data Processing Agreement"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className={errors.name ? 'border-red-500' : ''}
                disabled={isSubmitting}
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            {/* Category field removed */}

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="template-description">Description *</Label>
              <Textarea
                id="template-description"
                placeholder="Describe the template you'd like to see added. Include details about its purpose, content, and how it would be useful..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className={`min-h-[120px] ${errors.description ? 'border-red-500' : ''}`}
                disabled={isSubmitting}
              />
              {errors.description && (
                <p className="text-sm text-red-500">{errors.description}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {formData.description.length}/500 characters
              </p>
            </div>
          </div>

          {/* Info box removed */}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="min-w-[120px]"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
